import {
  Stack,
  Box,
  Typography,
  Button,
  Divider,
  useTheme,
  Select,
  MenuItem,
} from '@mui/material'
import { Error } from 'components/Error'
import PageWrapper from 'hoc/PageWrapper'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProcurementPlansTable } from './procurement-plans-table'
import { Download } from '@mui/icons-material'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
// @ts-ignore
import Excel from './procurement_plan_template.xlsx'
import {
  useProcurementPlanQuery,
  useProcurementPlansRelationsQuery,
} from 'services/procurementPlans'
import { Loading } from 'components/Loading'
import {
  EProcurementPlanStatus,
  IProcurementPlan,
  IProcurementPlanCorrelation,
} from 'types/procurementPlans'
import { TFunction } from 'i18next'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { DownloadPlanTemplate } from 'pages/procurement-plans/download-plan-template'

const renderCorrelationTitle = (
  t: TFunction,
  correlation?: IProcurementPlanCorrelation | null
) => {
  if (!correlation) {
    return ''
  }

  const date =
    correlation.status === EProcurementPlanStatus.PUBLISHED
      ? correlation.publishedAt
      : correlation.updatedAt

  if (!date) {
    return t(`procurement-status-${correlation.status}`)
  }

  return `${t(`procurement-status-${correlation.status}`)} - (${formatTimeStampToDate(date)})`
}

type Props = {
  procurementPlan: IProcurementPlan
}

export const ViewProcurementPlan = ({ procurementPlan }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const [searchParams] = useSearchParams({})
  const baseId = searchParams.get('baseId')

  const { data: correlations } = useProcurementPlansRelationsQuery(
    { id: procurementPlan?.id ?? 0 },
    { skip: !procurementPlan?.id }
  )
  const [selectedCorrelation, setSelectedCorrelation] =
    useState<IProcurementPlanCorrelation | null>(null)

  useEffect(() => {
    if (correlations && procurementPlan) {
      if (correlations.length === 1) {
        setSelectedCorrelation(correlations[0])
      } else if (correlations.length > 1) {
        setSelectedCorrelation(
          correlations.find(
            (c) =>
              c.id === procurementPlan.id &&
              c.correlationId === procurementPlan.correlationId
          ) ?? null
        )
      }
    }
  }, [correlations, procurementPlan])

  return (
    <PageWrapper>
      <Stack direction="row" alignItems="center">
        <Typography variant="h4" textTransform="uppercase" marginRight={8}>
          {t('procurement-plan')} {procurementPlan?.year}
        </Typography>
        <Stack alignItems="center" direction="row">
          <Typography variant="body1">{t('status')}:</Typography>
          <Typography fontWeight={700} color={theme.palette.primary.main}>
            {t(`procurement-status-${procurementPlan?.status}`)}
          </Typography>
        </Stack>
      </Stack>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginTop={2}
      >
        <Select
          variant="outlined"
          color="primary"
          size="small"
          value={selectedCorrelation}
          renderValue={(v) => renderCorrelationTitle(t, v)}
          sx={{
            backgroundColor: theme.palette.primary.main,
            color: '#FFFFFF',
            '.MuiSelect-iconOutlined': {
              color: '#FFFFFF',
            },
          }}
        >
          {correlations?.map((c) => (
            <MenuItem
              key={c.correlationId}
              onClick={() =>
                navigate(
                  `/portal/procurement-plans/${c.id}/view?baseId=${baseId || procurementPlan?.id}`
                )
              }
            >
              {renderCorrelationTitle(t, c)}
            </MenuItem>
          ))}
        </Select>

        <Box display="flex" gap={2}>
          <Button
            color="primary"
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            onClick={() =>
              navigate(`/portal/procurement-plans/${procurementPlan?.id}/edit`)
            }
          >
            {t('edit')}
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => navigate(`/portal/procurement-plans`)}
          >
            {t('back')}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box display="flex" justifyContent="flex-end" marginBottom={1.5} gap={2}>
        <DownloadPlanTemplate id={procurementPlan.id} />
      </Box>

      <ProcurementPlansTable
        canEdit={false}
        plans={procurementPlan?.procurementPlanItems ?? []}
      />
    </PageWrapper>
  )
}

export const ViewProcurementPlanPage = () => {
  const { procurementId } = useParams()

  const { data, isLoading, isError } = useProcurementPlanQuery(
    { id: procurementId ?? '' },
    { skip: !procurementId }
  )

  if (isLoading) return <Loading />

  if (isError) return <Error />

  if (data === undefined) {
    return <></>
  }

  return <ViewProcurementPlan procurementPlan={data} />
}
